import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Check, Loader2, AlertCircle, Shield, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_STORAGE_KEY = 'cp_admin_token';

const DISCIPLINE_OPTIONS = [
  'Skeet', 'Skeet Olympique', 'DTL',
  'Fosse Olympique', 'Fosse Américaine',
  'Parcours de Chasse', 'Compak Sporting', 'Helice',
];
const ALLOWED_MIME_PREFIXES = ['image/', 'application/pdf'];

export default function AdminCountryEventNew() {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  const [form, setForm] = useState({
    name: '', organizer: '', address: '', city: '', postal_code: '', department: '',
    date_start: '', date_end: '', opening_hours: '', disciplines: [],
    price_info: '', description: '', phone: '', email: '', website: '', contact_email: '',
  });
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractInfo, setExtractInfo] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesAck, setDuplicatesAck] = useState(false);

  // Debounced duplicate check
  useEffect(() => {
    const city = form.city.trim();
    const postal = form.postal_code.trim();
    const ds = form.date_start.trim();
    if (!postal && !city) { setDuplicates([]); return; }
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (form.name) params.set('name', form.name);
        if (city) params.set('city', city);
        if (postal) params.set('postal_code', postal);
        if (form.department) params.set('department', form.department);
        if (ds) params.set('date_start', ds);
        if (form.date_end) params.set('date_end', form.date_end);
        const r = await axios.get(`${API}/country-events/check-duplicate?${params}`);
        setDuplicates(Array.isArray(r.data?.duplicates) ? r.data.duplicates : []);
        setDuplicatesAck(false);
      } catch { setDuplicates([]); }
    }, 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.city, form.postal_code, form.department, form.date_start, form.date_end]);

  if (!adminToken) {
    window.location.href = '/admin/country-events';
    return null;
  }

  function update(key, value) { setForm((f) => ({ ...f, [key]: value })); }
  function toggleDiscipline(d) {
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(d) ? f.disciplines.filter((x) => x !== d) : [...f.disciplines, d],
    }));
  }

  async function extractPoster() {
    if (!posterFile || extracting) return;
    setExtracting(true); setError(''); setExtractInfo('');
    try {
      const data = new FormData();
      data.append('poster', posterFile);
      const r = await axios.post(`${API}/country-events/extract-poster`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      const extracted = r.data?.data || {};
      const count = r.data?.fields_extracted || 0;
      setForm((f) => {
        const next = { ...f };
        for (const k of Object.keys(extracted)) {
          if (k === 'disciplines') {
            const existing = new Set(f.disciplines || []);
            (extracted.disciplines || []).forEach((d) => {
              if (DISCIPLINE_OPTIONS.includes(d)) existing.add(d);
            });
            next.disciplines = Array.from(existing);
          } else if (extracted[k] && (!f[k] || String(f[k]).trim() === '')) {
            next[k] = extracted[k];
          }
        }
        return next;
      });
      setExtractInfo(count
        ? `✨ ${count} champ${count > 1 ? 's' : ''} extrait${count > 1 ? 's' : ''}.`
        : "L'IA n'a pas pu lire cette image. Vérifiez qu'il s'agit bien d'une affiche lisible (texte visible, image nette) ou remplissez à la main."
      );
    } catch (err) {
      setExtractInfo(err.response?.data?.error || "Extraction IA indisponible.");
    } finally {
      setExtracting(false);
    }
  }
  function handleFile(e) {
    const file = e.target.files?.[0];
    setError('');
    if (!file) { setPosterFile(null); setPosterPreview(null); return; }
    if (!ALLOWED_MIME_PREFIXES.some((p) => file.type.startsWith(p))) {
      setError('Format non supporté. Utilisez une image ou un PDF.'); return;
    }
    if (file.size > 15 * 1024 * 1024) { setError('Fichier trop volumineux (max 15 Mo).'); return; }
    setPosterFile(file);
    if (file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif') {
      const reader = new FileReader();
      reader.onload = (ev) => setPosterPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPosterPreview(null);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || form.name.trim().length < 3) {
      setError("Le nom de l'évènement est requis."); return;
    }
    if (!form.postal_code.trim() && !form.department.trim()) {
      setError('Code postal ou département requis.'); return;
    }
    if (duplicates.length > 0 && !duplicatesAck) {
      setError("Évènement similaire détecté. Cochez la confirmation pour publier quand même.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'disciplines') data.append(k, v.join(','));
      else data.append(k, v);
    });
    data.append('notify_email', notifyEmail ? 'true' : 'false');
    if (posterFile) data.append('poster', posterFile);
    try {
      await axios.post(`${API}/admin/country-events`, data, {
        headers: { 'X-Admin-Token': adminToken, 'Content-Type': 'multipart/form-data' },
      });
      navigate('/admin/country-events?created=1');
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/admin/country-events" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Modération
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFC107]/15 border border-[#FFC107]/40 text-[#FFC107] text-xs font-bold">
          <Shield className="w-3.5 h-3.5" /> Mode admin · Publication immédiate
        </div>
        <h1 className="text-3xl sm:text-4xl font-black mb-2">Déclarer un ball-trap</h1>
        <p className="text-[#C8C8C8] mb-6 leading-relaxed text-sm max-w-2xl">
          Cet évènement sera publié <strong>immédiatement</strong> sur la carte (statut « validé »).
          Si l'email de contact correspond à un compte ClayPulse existant, l'évènement y sera rattaché automatiquement.
        </p>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 flex items-start gap-3" data-testid="admin-new-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /><p className="text-sm">{error}</p>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="mb-6 rounded-xl bg-amber-500/10 border border-amber-500/40 p-4" data-testid="admin-dup-banner">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-300" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-200">
                  Évènement similaire déjà déclaré ({duplicates.length})
                </p>
                <p className="text-xs text-[#C8C8C8] mt-1 leading-relaxed">
                  Vérifiez la liste pour éviter un doublon. Confirmez ci-dessous pour publier malgré tout.
                </p>
              </div>
            </div>
            <ul className="space-y-2 mb-3">
              {duplicates.map((d) => (
                <li key={d.id} className="text-xs flex items-center justify-between gap-2 p-2 rounded-lg bg-black/30 border border-white/5">
                  <div className="min-w-0">
                    <div className="font-bold text-white truncate">{d.name || '(sans nom)'}</div>
                    <div className="text-[11px] text-[#888]">
                      {[d.city, d.postal_code, d.date_start, d.status === 'pending' ? '· en attente' : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
                    {d._match_score}%
                  </span>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-xs text-[#C8C8C8] cursor-pointer">
              <input type="checkbox" checked={duplicatesAck} onChange={(e) => setDuplicatesAck(e.target.checked)}
                className="w-4 h-4 accent-amber-400" data-testid="admin-dup-ack" />
              Je confirme vouloir publier malgré tout.
            </label>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" data-testid="admin-new-form">
          <Field label="Nom de l'évènement *">
            <input required value={form.name} onChange={(e) => update('name', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
              data-testid="admin-new-name" />
          </Field>
          <Field label="Club ou organisateur">
            <input value={form.organizer} onChange={(e) => update('organizer', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => update('description', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none resize-y" />
          </Field>
          <Field label="Adresse">
            <input value={form.address} onChange={(e) => update('address', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code postal *">
              <input value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)}
                maxLength={5}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                data-testid="admin-new-postal" />
            </Field>
            <Field label="Ville">
              <input value={form.city} onChange={(e) => update('city', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date de début">
              <input type="date" value={form.date_start} onChange={(e) => update('date_start', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
            <Field label="Date de fin">
              <input type="date" value={form.date_end} onChange={(e) => update('date_end', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
          </div>
          <Field label="Horaires">
            <input value={form.opening_hours} onChange={(e) => update('opening_hours', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
          </Field>
          <Field label="Tarifs">
            <input value={form.price_info} onChange={(e) => update('price_info', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
          </Field>

          <fieldset>
            <legend className="text-xs font-semibold text-[#C8C8C8] mb-2">Disciplines proposées</legend>
            <div className="flex flex-wrap gap-2">
              {DISCIPLINE_OPTIONS.map((d) => {
                const active = form.disciplines.includes(d);
                return (
                  <button type="button" key={d} onClick={() => toggleDiscipline(d)}
                    className={`px-3.5 py-2 text-sm rounded-full border transition-all ${active
                      ? 'bg-[#FFC107] border-[#FFC107] text-black font-bold'
                      : 'bg-white/5 border-white/10 text-[#C8C8C8] hover:border-[#FFC107]/40'}`}>
                    {d}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Téléphone (public)">
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
            <Field label="Email (public)">
              <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
            </Field>
          </div>
          <Field label="Site web ou Facebook">
            <input type="url" value={form.website} onChange={(e) => update('website', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none" />
          </Field>

          <Field label="Email de l'organisateur (privé, pour rattacher l'évènement à son compte)">
            <input type="email" value={form.contact_email} onChange={(e) => update('contact_email', e.target.value)}
              placeholder="organisateur@example.com"
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
              data-testid="admin-new-contact-email" />
          </Field>

          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">Affiche (optionnel)</legend>
            <label className="block cursor-pointer">
              <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" data-testid="admin-new-poster" />
              <div className="border-2 border-dashed border-white/15 rounded-xl p-5 hover:border-[#FFC107]/50 transition-colors text-center">
                {posterPreview ? (
                  <>
                    <img src={posterPreview} alt="Aperçu" className="max-h-40 mx-auto rounded-lg shadow-lg mb-2" />
                    <p className="text-xs text-[#888]">Cliquez pour changer</p>
                  </>
                ) : posterFile ? (
                  <p className="text-sm text-white">{posterFile.name}</p>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/5 text-white/60 mb-2">
                      <Upload className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-white">Ajouter une affiche</p>
                    <p className="text-xs text-[#888] mt-1">Images & PDF · 15 Mo max</p>
                  </>
                )}
              </div>
            </label>

            {posterFile && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={extractPoster}
                  disabled={extracting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-400/40 text-purple-200 font-bold hover:from-purple-600/30 hover:to-pink-600/30 hover:text-white disabled:opacity-60 transition-all"
                  data-testid="admin-extract-btn"
                >
                  {extracting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> L'IA lit l'affiche…</>
                  ) : extractInfo ? (
                    <><RefreshCw className="w-4 h-4" /> Réessayer l'extraction</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> ✨ Auto-remplir avec l'IA</>
                  )}
                </button>
                {extractInfo && (
                  <p className={`mt-2 text-xs text-center ${extractInfo.startsWith('✨') ? 'text-purple-300' : 'text-[#888]'}`}>
                    {extractInfo}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          <label className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10 cursor-pointer hover:border-[#FFC107]/40 transition-colors" data-testid="admin-new-notify-row">
            <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#FFC107]" data-testid="admin-new-notify-cb" />
            <div className="flex-1 text-sm">
              <div className="font-bold mb-0.5">Notifier l'organisateur par email</div>
              <p className="text-xs text-[#A0A0A0] leading-relaxed">
                Envoie un email à l'adresse ci-dessus avec un lien pour créer son compte ClayPulse et récupérer la gestion de l'évènement.
                Désactivé si aucun email saisi.
              </p>
            </div>
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <Link to="/admin/country-events" className="text-sm text-white/60 hover:text-white">
              Annuler
            </Link>
            <button
              type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-60 hover:scale-[1.03] transition-transform"
              data-testid="admin-new-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {submitting ? 'Publication…' : 'Publier l\'évènement'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#C8C8C8] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
